import { redirect } from "next/navigation";
import { createClient } from "@/lib/supabase/server";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { RatingsList } from "@/components/ratings-list";
import { RatingsSummary } from "@/components/ratings-summary";
import Link from "next/link";

interface PageProps {
  params: { userId: string };
}

export default async function UserRatingsPage({ params }: PageProps) {
  const supabase = await createClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    redirect("/auth/login");
  }

  const userId = params.userId;

  // Fetch the profile we're viewing ratings for
  const { data: profile } = await supabase
    .from("profiles")
    .select("id, name, type")
    .eq("id", userId)
    .single();

  if (!profile) {
    redirect("/dashboard");
  }

  // Fetch ratings with profile information
  const { data: ratings } = await supabase
    .from("ratings")
    .select(`
      *,
      from_profile:profiles!ratings_from_id_fkey(
        id,
        name,
        avatar_url
      )
    `)
    .eq("to_id", userId)
    .order("created_at", { ascending: false });

  // Calculate average rating
  const averageRating = ratings?.length 
    ? ratings.reduce((sum, r) => sum + r.score, 0) / ratings.length 
    : 0;

  const pageTitle = `${profile.name || 'User'}'s Reviews`;

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-6">
        <div className="mb-6 flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link href="/dashboard">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
        </div>

        {/* Ratings Summary */}
        <RatingsSummary 
          averageRating={averageRating}
          totalRatings={ratings?.length || 0}
          className="mb-6"
        />

        {/* All Ratings */}
        <RatingsList 
          ratings={ratings || []}
          title="All Reviews"
          emptyMessage="No reviews yet. Reviews appear here after work is completed."
        />
      </main>
    </div>
  );
}